/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Stack from "@mui/material/Stack";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/id";
import {
  FIND_EXTERNAL_ORDER_BY_ID,
  UPDATE_EXTERNAL_ORDER_PRINTED_AT,
} from "../../graphql/ExternalOrder";
import MyBreadcrumbs from "../MyBreadcrumbs";
import MyLoading from "../MyLoading";
import GlobalContext from "../../utils/GlobalContext";
import ExternalOrderPDF from "../PDF/ExternalOrderPDF";
import { chipColor } from "../../utils";

dayjs.extend(customParseFormat);
dayjs.locale("id");

const formatRupiah = (value) =>
  `Rp. ${Number(value || 0).toLocaleString("id-ID")}`;

const formatDate = (value) => {
  if (!value) return "-";
  const dayFirstDate = /^\d{2}-\d{2}-\d{4}/.test(value);
  const format = value.length > 10 ? "DD-MM-YYYY HH:mm:ss" : "DD-MM-YYYY";
  const d = dayFirstDate ? dayjs(value, format, true) : dayjs(value);
  return d.isValid() ? d.format("DD MMMM YYYY") : value;
};

const SectionTitle = ({ children }) => (
  <Typography
    variant="subtitle1"
    fontWeight={600}
    color="text.primary"
    sx={{ mb: 1.5 }}
  >
    {children}
  </Typography>
);

const InfoItem = ({ label, children }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {children || "-"}
    </Typography>
  </Grid>
);

const SummaryRow = ({ label, value, bold, highlight }) => (
  <React.Fragment>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        py: 1,
      }}
    >
      <Typography
        variant="body2"
        color={highlight ? "primary" : "text.secondary"}
        fontWeight={bold || highlight ? 600 : 400}
      >
        {label}
      </Typography>
      <Typography
        variant={highlight ? "subtitle1" : "body2"}
        fontWeight={bold || highlight ? 700 : 500}
        color={highlight ? "primary" : "text.primary"}
      >
        {value}
      </Typography>
    </Box>
    <Divider />
  </React.Fragment>
);

const ExternalOrderDetail = (props) => {
  const { id } = props?.match?.params;
  const { setGlobalLoading, setSnack } = React.useContext(GlobalContext);
  const [order, setOrder] = React.useState(null);
  const [notFound, setNotFound] = React.useState(false);

  const { loading, data } = useQuery(FIND_EXTERNAL_ORDER_BY_ID, {
    variables: { id },
    fetchPolicy: "network-only",
    onError: (e) => {
      console.log("error getting external order:", e);
      setSnack({
        variant: "error",
        message: "Data Nota gagal dimuat",
        opened: true,
      });
    },
  });

  React.useEffect(() => {
    setGlobalLoading(loading);
  }, [loading]);

  React.useEffect(() => {
    if (data) {
      if (data.findExternalOrderById) {
        setOrder(data.findExternalOrderById);
      } else {
        setNotFound(true);
      }
    }
  }, [data]);

  const details = order?.external_order_details || [];

  const totalMutation = details.reduce(
    (n, x) => Number(n) + Number(x.mutation || 0),
    0,
  );

  const [updatePrintedAt] = useMutation(UPDATE_EXTERNAL_ORDER_PRINTED_AT);

  const handlePrint = async (withFee) => {
    let printed_at = order?.printed_at;

    if (!printed_at) {
      try {
        const res = await updatePrintedAt({
          variables: { id: order.id, printed_at: dayjs().format("YYYY-MM-DD") },
        });
        printed_at = res?.data?.updateExternalOrderPrintedAt?.printed_at;
        setOrder({ ...order, printed_at });
      } catch (e) {
        console.log("error update printed_at: ", e);
      }
    }

    ExternalOrderPDF(
      { ...order, printed_at: printed_at || dayjs().format("DD-MM-YYYY") },
      withFee,
    );
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          bgcolor: "background.paper",
          borderRadius: 1,
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <MyBreadcrumbs data={["Transaksi", "Nota", "Detail"]} />
        </Box>
        <Stack direction="row" spacing={1} sx={{ p: 0.5 }}>
          <Button
            component={Link}
            to={"/admin/external_order/"}
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
          >
            Kembali
          </Button>
          {order && (
            <React.Fragment>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PrintIcon />}
                onClick={() => handlePrint(false)}
              >
                Cetak Tagihan
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<PrintIcon />}
                onClick={() => handlePrint(true)}
              >
                Cetak Tagihan + Biaya
              </Button>
            </React.Fragment>
          )}
        </Stack>
      </Box>

      {loading && <MyLoading />}

      {notFound && !loading && (
        <Paper sx={{ p: 4, textAlign: "center", maxWidth: 1200, mx: "auto" }}>
          <Typography variant="h6" gutterBottom>
            Data tidak ditemukan
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Data Nota yang anda cari tidak tersedia atau sudah dihapus.
          </Typography>
          <Button
            component={Link}
            to={"/admin/external_order/"}
            variant="contained"
            size="small"
          >
            Kembali ke Daftar Nota
          </Button>
        </Paper>
      )}

      {order && !loading && (
        <Box sx={{ maxWidth: 1200, mx: "auto", pb: 3 }}>
          {/* KARTU JUDUL */}
          <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {order.eoid}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Dibuat pada {formatDate(order.created_at)}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Chip
                  label={order.company}
                  color={chipColor(order.company)}
                  size="small"
                />
                <Chip label={order.type} variant="outlined" size="small" />
              </Stack>
            </Stack>
          </Paper>

          {/* INFORMASI UMUM */}
          <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
            <SectionTitle>Informasi Umum</SectionTitle>
            <Divider sx={{ mb: 2.5 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Pelanggan
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {order.client?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {order.client?.address}
                  {order.client?.regency ? `, ${order.client.regency}` : ""}
                </Typography>
              </Grid>
              <InfoItem label="Marketing">{order.employee?.name}</InfoItem>
              <InfoItem label="Perusahaan">{order.company}</InfoItem>
              <InfoItem label="Cawu & Tahun">
                {`Cawu ${order.quarterly} - Tahun ${order?.year}`}
              </InfoItem>
              <InfoItem label="Tipe">{order.type}</InfoItem>
              <InfoItem label="Tanggal Cetak">
                {order.printed_at || "-"}
              </InfoItem>
            </Grid>
          </Paper>

          {/* RINCIAN NOTA */}
          <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
            <SectionTitle>Rincian Nota</SectionTitle>
            <Divider sx={{ mb: 2.5 }} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>No</TableCell>
                    <TableCell>No Referensi</TableCell>
                    <TableCell>Tanggal Transaksi</TableCell>
                    <TableCell align="right">Nominal</TableCell>
                    <TableCell align="right">Pajak</TableCell>
                    <TableCell align="right">PPh SIPLAH</TableCell>
                    <TableCell align="right">Biaya VA</TableCell>
                    <TableCell align="right">Biaya Transaksi</TableCell>
                    <TableCell align="right">Mutasi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.map((x, i) => (
                    <TableRow key={x.id} hover>
                      <TableCell>{i + 1}.</TableCell>
                      <TableCell>{x.no_reference}</TableCell>
                      <TableCell>{formatDate(x.transaction_at)}</TableCell>
                      <TableCell align="right">
                        {formatRupiah(x.nominal)}
                      </TableCell>
                      <TableCell align="right">{formatRupiah(x.tax)}</TableCell>
                      <TableCell align="right">
                        {formatRupiah(x.pph_fee)}
                      </TableCell>
                      <TableCell align="right">
                        {formatRupiah(x.va_fee)}
                      </TableCell>
                      <TableCell align="right">
                        {formatRupiah(x.transaction_fee)}
                      </TableCell>
                      <TableCell align="right">
                        {formatRupiah(x.mutation)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      sx={{ fontWeight: 700, borderBottom: "none" }}
                    >
                      Total
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, borderBottom: "none" }}
                    >
                      {formatRupiah(order.total_payment_fee)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, borderBottom: "none" }}
                    >
                      {formatRupiah(order.tax)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, borderBottom: "none" }}
                    >
                      {formatRupiah(order.total_pph_fee)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, borderBottom: "none" }}
                    >
                      {formatRupiah(order.total_va_fee)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, borderBottom: "none" }}
                    >
                      {formatRupiah(order.total_transaction_fee)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, borderBottom: "none" }}
                    >
                      {formatRupiah(totalMutation)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Paper>

          {/* RINGKASAN PEMBAYARAN */}
          <Paper sx={{ p: { xs: 2, md: 3 } }}>
            <SectionTitle>Ringkasan Pembayaran</SectionTitle>
            <Divider sx={{ mb: 2.5 }} />
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <SummaryRow
                  label="Uang Masuk"
                  value={formatRupiah(order.total_payment_fee)}
                />
                <SummaryRow
                  label="Nominal Pembelanjaan"
                  value={formatRupiah(order.total_sales)}
                />
                <SummaryRow
                  label="Total Pajak"
                  value={formatRupiah(order.tax)}
                />
                <SummaryRow
                  label="Total PPh SIPLAH"
                  value={formatRupiah(order.total_pph_fee)}
                />
                <SummaryRow
                  label="Total Biaya VA"
                  value={formatRupiah(order.total_va_fee)}
                />
                <SummaryRow
                  label="Total Biaya Transaksi / Beda Bank"
                  value={formatRupiah(order.total_transaction_fee)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SummaryRow
                  label={`Platform Fee${
                    order.is_custom_fee && order.custom_fee_percentage
                      ? ` (${order.custom_fee_percentage}%)`
                      : ""
                  }`}
                  value={formatRupiah(order.admin_fee)}
                />
                {order.additional && (
                  <SummaryRow
                    label="Additional Fee"
                    value={formatRupiah(order.additional_fee)}
                  />
                )}
                <SummaryRow
                  label="Expense Fee"
                  value={formatRupiah(order.tax_admin_fee)}
                />
                <SummaryRow
                  label="Kembalian"
                  value={formatRupiah(order.refund)}
                  highlight
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}
    </React.Fragment>
  );
};

export default ExternalOrderDetail;
